import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";

import {
  getFullWorkByParentId,
  getMasterWorkByOrgId,
  getWork,
} from "@/actions/work";
import { type WorkModel } from "@/api";
import { queryKeys } from "@/constants/query";
import { isValidOrgId } from "@/lib/utils";
import { type WorkTreeModel } from "@/models/WorkTreeModel";
import type { NavItem } from "@/types";

import { invalidateOrgProjectListQueries } from "@/lib/invalidate-org-project-list-queries";

export const getProjectItemsQuery = async (orgId: string) => {
  try {
    if (!isValidOrgId(orgId)) return [];
    const projectItems = await getMasterWorkByOrgId(orgId);
    return projectItems;
  } catch {
    toast.error("Error fetching work items");
    return [];
  }
};

export const useProjectItems = () => {
  const params = useParams();
  const orgId = params?.orgId as string;

  const {
    data: projectItems,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.getProjectItems(orgId),
    queryFn: () => getProjectItemsQuery(orgId),
    enabled: isValidOrgId(orgId),
  });

  return {
    projectItems,
    isLoading,
    isError,
    error,
    fetchWorkItems: refetch,
  };
};

/**
 * When the org project list gains a new root project (e.g. after chat creates one),
 * navigate to that project. Mount once under the org shell (e.g. DashboardNav), not
 * per sidebar row — otherwise an empty project list never subscribed and the redirect
 * never ran.
 */
export const useProjectCreatedRedirect = () => {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const orgId = params?.orgId as string;
  const projectId =
    typeof params?.projectId === "string" ? params.projectId : undefined;
  const previousDataRef = useRef<WorkModel[]>();

  const { data: projectItems } = useQuery({
    queryKey: queryKeys.getProjectItems(orgId),
    queryFn: () => getProjectItemsQuery(orgId),
    enabled: isValidOrgId(orgId),
  });

  useEffect(() => {
    if (!projectItems || !previousDataRef.current) {
      previousDataRef.current = projectItems;
      return;
    }

    const previous = previousDataRef.current;
    const current = projectItems;

    const newItems = current.filter(
      (curr) => !previous.some((prev) => prev.id === curr.id),
    );

    const removedItems = previous.filter(
      (prev) => !current.some((curr) => curr.id === prev.id),
    );

    if (newItems.length > 0) {
      void invalidateOrgProjectListQueries(queryClient, orgId, {
        openProjectId: projectId,
      });
      router.push(
        `/${orgId}/projects/${newItems[0].id}?${searchParams.toString()}`,
      );
    }

    if (removedItems.length > 0) {
      void invalidateOrgProjectListQueries(queryClient, orgId, {
        openProjectId: projectId,
      });
      if (current.length) {
        router.push(
          `/${orgId}/projects/${current[0].id}?${searchParams.toString()}`,
        );
      } else {
        router.push(`/${orgId}/dashboard?${searchParams.toString()}`);
      }
    }

    previousDataRef.current = projectItems;
  }, [projectItems, projectId, queryClient, router, searchParams, orgId]);
};

const getProjectNavItemsQuery = async (orgId: string) => {
  try {
    if (!isValidOrgId(orgId)) return [];
    const projectItems = await getMasterWorkByOrgId(orgId);
    return projectItems
      ?.sort((a, b) => {
        // Prefer updatedDate; fall back to createdDate
        const aDate = a.updatedDate ?? a.createdDate;
        const bDate = b.updatedDate ?? b.createdDate;

        // Handle null or undefined dates
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1; // Move items without any date to the end
        if (!bDate) return -1;

        // Sort in descending order (newest first)
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      })
      ?.map((work) => ({
        label: work.name || "Unnamed Work",
        href: `/projects/${work.id}`,
        icon: "project",
        title: work.name || "Work Item",
        queryObj: { view: "app" },
      })) as NavItem[];
  } catch {
    toast.error("Error fetching work items");
    return [];
  }
};

export const useProjectNavItems = () => {
  const params = useParams();
  const orgId = params?.orgId as string;

  const {
    data: projectItems,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.getProjectNavItems(orgId),
    queryFn: () => getProjectNavItemsQuery(orgId),
    enabled: isValidOrgId(orgId),
  });

  return {
    projectItems,
    isLoading,
    isError,
    error,
    fetchWorkItems: refetch,
  };
};

export const useProjectItem = () => {
  const params = useParams();
  const projectId = params?.projectId as string;

  const {
    data: projectItem,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.getProjectItem(projectId),
    queryFn: () => getWork(projectId),
  });

  return {
    projectItem,
    isLoading,
    isError,
    error,
    fetchWorkItems: refetch,
  };
};

const mapParentRecursive = (workItems: WorkTreeModel[]) => {
  const options: { value: string; label: string }[] = [];
  workItems.forEach((item) => {
    options.push({
      value: item.id || "",
      label: item.name || "Unnamed Work Item",
    });

    if (item.subRows) {
      options.push(...mapParentRecursive(item.subRows));
    }
  });
  return options;
};

export const getWorkItemAllParentOptions = (
  projectWorkItems: WorkTreeModel[],
  currentWorkItem: WorkModel,
  search = "",
) => {
  const options = mapParentRecursive(projectWorkItems);

  const filteredOptions = options
    .filter(
      (item) =>
        !search || item.label?.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      // Sort alphabetically but keep the selected item at the top
      const aIsSelected = a.value === currentWorkItem.parentId;
      const bIsSelected = b.value === currentWorkItem.parentId;

      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;

      return 0;
    });

  return [
    {
      value: "",
      label: "Unassigned",
    },
    ...filteredOptions,
  ];
};

export const getParentWorkOptions = (
  projectWorkItems: WorkTreeModel[],
  currentWorkItem: WorkModel,
  search: string = "",
) => {
  // Filter out the current work item (can't be its own parent)
  // and any items that are already children of this work item (to prevent circular references)
  const filteredWorkItems = projectWorkItems.filter(
    (item) =>
      item.id !== currentWorkItem.id && item.parentId !== currentWorkItem.id,
  );

  // Convert to Option format and filter by search term
  const options = filteredWorkItems
    .filter(
      (item) =>
        !search || item.name?.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      // Sort to show the selected item (current parent) at the top
      const aIsSelected = a.id === currentWorkItem.parentId;
      const bIsSelected = b.id === currentWorkItem.parentId;

      if (aIsSelected && !bIsSelected) return -1;
      if (!aIsSelected && bIsSelected) return 1;

      // If neither or both are selected, maintain original order
      return 0;
    })
    .map((item) => ({
      value: item.id || "",
      label: item.name || "Unnamed Work Item",
    }));

  // Add 'No Parent' option at the beginning
  options.unshift({
    value: "",
    label: "Unassigned",
  });

  return options;
};

export const useFullWorkItems = () => {
  const params = useParams();
  const projectId = params?.projectId as string;

  const {
    data: fullWorkItems,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["fullWorkItems", projectId],
    queryFn: () => getFullWorkByParentId(projectId),
    enabled: !!projectId,
  });

  return {
    fullWorkItems,
    isLoading,
    isError,
    error,
    fetchFullWorkItems: refetch,
  };
};
